<template>
    <div
        class="bottom-fixed"
        :class="{
            'bottom-fixed--safe-area': shouldUseSafeArea,
            'bottom-fixed--keyboard-visible': staticOnKeyboard && keyboardVisible,
        }"
        :style="bottomFixedStyle"
        ref="rootEl"
    >
        <!-- 顶部渐变蒙层 -->
        <div v-if="showMask" class="bottom-fixed__mask" :class="{ hidden: isScrolledToBottom }" ref="maskEl" />

        <div class="bottom-fixed__content">
            <slot></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BottomFixed',
    props: {
        useSafeArea: { type: Boolean, default: true },
        zIndex: { type: Number, default: 1000 },
        autoAdjustOnKeyboard: { type: Boolean, default: true },
        staticOnKeyboard: { type: Boolean, default: false }, // 输入时是否变成 static，默认 false
        value: { type: Number, default: 0 }, // v-model 支持（通过 input 事件）
        maskHeight: { type: Number, default: 0 }, // .sync 支持
        showMask: { type: Boolean, default: true }, // 是否显示蒙层
    },
    emits: ['input', 'height-change', 'mask-height-change', 'update:maskHeight'],
    data() {
        return {
            keyboardVisible: false,
            originalViewportHeight: 0,
            resizeTimeout: null,
            isScrolledToBottom: false,
            height: 0,
            internalMaskHeight: 0,
            resizeObserver: null,
            mutationObserver: null,
            keyboardTransitioning: false, // 键盘状态变化中标志，避免 ResizeObserver 干扰
        }
    },
    computed: {
        // inline style：非键盘时保持 fixed（CSS 中定义）；键盘打开时根据 staticOnKeyboard prop 决定是否给出 position: static 的 inline 覆盖（双保险）
        bottomFixedStyle() {
            const base = { zIndex: this.zIndex }
            if (this.staticOnKeyboard && this.keyboardVisible) {
                return Object.assign({}, base, {
                    position: 'static',
                })
            }
            // fixed 时交给 CSS 控制 bottom: 0
            return base
        },

        // 在键盘打开时禁用 safe area padding（常见需求）- 仅当 staticOnKeyboard 为 true 时才生效
        shouldUseSafeArea() {
            return this.useSafeArea && !(this.staticOnKeyboard && this.keyboardVisible)
        },

        // v-model / currentHeight（保持原语义）
        currentHeight: {
            get() {
                return this.height
            },
            set(value) {
                this.height = value
                this.emitHeightChange()
            },
        },
    },
    watch: {
        maskHeight: {
            handler(newVal) {
                this.internalMaskHeight = newVal
            },
            immediate: true,
        },
        // 监听 keyboardVisible：键盘状态变化时重新计算高度
        keyboardVisible() {
            // 键盘状态变化时，重新计算并发送真实高度
            this.$nextTick(() => {
                this.updateHeight()
            })
        },
    },
    mounted() {
        // 只在移动 H5 场景启用键盘自适应
        if (this.autoAdjustOnKeyboard && this.isMobileDevice()) {
            this.initKeyboardDetection()
        }
        this.initScrollDetection()
        this.initHeightDetection()
    },
    beforeDestroy() {
        if (this.autoAdjustOnKeyboard && this.isMobileDevice()) {
            this.removeKeyboardDetection()
        }
        this.removeScrollDetection()
        this.removeHeightDetection()
    },
    methods: {
        // ----- 辅助：移动端检测 -----
        isMobileDevice() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        },

        // 初始化键盘检测（H5：以 focusin/out 为主）
        initKeyboardDetection() {
            // 记录一个初始高度备份（备用）
            this.originalViewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight

            // 现代浏览器（可选）保持 visualViewport 的监听（不作为主判定）
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this.handleViewportResize)
            } else {
                window.addEventListener('resize', this.handleWindowResize)
            }

            // 主判定：focusin / focusout（更适用于 H5 各类 WebView）
            document.addEventListener('focusin', this.handleFocusIn)
            document.addEventListener('focusout', this.handleFocusOut)
        },

        removeKeyboardDetection() {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this.handleViewportResize)
            } else {
                window.removeEventListener('resize', this.handleWindowResize)
            }
            document.removeEventListener('focusin', this.handleFocusIn)
            document.removeEventListener('focusout', this.handleFocusOut)
            // 清理 CSS 变量（如果有）
            if (this.$el) {
                this.$el.style.removeProperty('--keyboard-height')
            }
        },

        // visualViewport resize（仅用于保持备用状态）
        handleViewportResize() {
            // 不以 visualViewport 为主判定，仅用于可能的高度同步
        },

        handleWindowResize() {
            clearTimeout(this.resizeTimeout)
            this.resizeTimeout = setTimeout(() => {
                // 备用：在没有 visualViewport 的环境做最低限度同步
                this.originalViewportHeight = window.innerHeight
            }, 120)
        },

        // focusin → 认为键盘要弹出（H5 场景）
        handleFocusIn(e) {
            const el = e && e.target
            if (!el) return
            const tag = el.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable) {
                // 延迟一点，避免切换输入时的抖动
                setTimeout(() => {
                    this.keyboardTransitioning = true
                    this.keyboardVisible = true
                    // 给键盘动画留出时间，避免 ResizeObserver 干扰
                    setTimeout(() => {
                        this.keyboardTransitioning = false
                    }, 300)
                }, 50)
            }
        },

        // focusout → 检查当前是否还有输入仍处于焦点
        handleFocusOut() {
            setTimeout(() => {
                const active = document.activeElement
                const stillInput = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)

                if (!stillInput) {
                    // 没有输入聚焦，恢复 fixed
                    this.keyboardTransitioning = true
                    this.keyboardVisible = false
                    // 键盘关闭时重新计算实际高度（watch 会触发 updateHeight，但这里再确保）
                    this.$nextTick(() => {
                        this.updateHeight()
                        // 给键盘动画留出时间
                        setTimeout(() => {
                            this.keyboardTransitioning = false
                        }, 300)
                    })
                }
            }, 120)
        },

        // ----- scroll / height / mask（保留原逻辑） -----
        initScrollDetection() {
            window.addEventListener('scroll', this.handleScroll, { passive: true })
            this.checkScrollPosition()
        },
        removeScrollDetection() {
            window.removeEventListener('scroll', this.handleScroll)
        },
        handleScroll() {
            this.checkScrollPosition()
        },
        checkScrollPosition() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const windowHeight = window.innerHeight
            const documentHeight = document.documentElement.scrollHeight
            // 只有当页面可滚动时，才判断是否滚动到底部
            const isScrollable = documentHeight > windowHeight
            this.isScrolledToBottom = isScrollable && scrollTop + windowHeight >= documentHeight - 24
        },

        initHeightDetection() {
            this.$nextTick(() => {
                if (window.ResizeObserver) {
                    this.resizeObserver = new ResizeObserver(this.handleResize)
                    this.resizeObserver.observe(this.$el)
                    if (this.$refs.maskEl) this.resizeObserver.observe(this.$refs.maskEl)
                } else {
                    this.initMutationObserver()
                }
                this.updateHeight()
                this.updateMaskHeight()
            })
        },
        removeHeightDetection() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
                this.resizeObserver = null
            }
            if (this.mutationObserver) {
                this.mutationObserver.disconnect()
                this.mutationObserver = null
            }
        },
        handleResize(entries) {
            for (const entry of entries) {
                if (entry.target === this.$el) this.updateHeight()
                else if (entry.target === this.$refs.maskEl) this.updateMaskHeight()
            }
        },
        updateHeight() {
            // 如果正在处理键盘状态变化，暂时不更新高度，避免与手动设置冲突
            if (this.keyboardTransitioning) {
                return
            }

            if (this.$el) {
                const newHeight = this.$el.offsetHeight
                if (newHeight !== this.height) {
                    this.height = newHeight
                    this.emitHeightChange()
                }
            }
        },
        updateMaskHeight() {
            if (this.$refs.maskEl) {
                const newMaskHeight = this.$refs.maskEl.offsetHeight
                if (newMaskHeight !== this.internalMaskHeight) {
                    this.internalMaskHeight = newMaskHeight
                    this.emitMaskHeightChange()
                }
            }
        },
        // 发送高度变化事件：始终发送真实高度
        emitHeightChange() {
            this.$emit('input', this.height)
            this.$emit('height-change', this.height)
        },
        emitMaskHeightChange() {
            this.$emit('mask-height-change', this.internalMaskHeight)
            this.$emit('update:maskHeight', this.internalMaskHeight)
        },
        initMutationObserver() {
            if (window.MutationObserver) {
                this.mutationObserver = new MutationObserver(() => {
                    this.updateHeight()
                    this.updateMaskHeight()
                })
                this.mutationObserver.observe(this.$el, {
                    attributes: true,
                    attributeFilter: ['style', 'class'],
                    subtree: true,
                    childList: true,
                })
            }
        },
    },
}
</script>

<style lang="less" scoped>
// .bottom-fixed--keyboard-visible {
//     .bottom-fixed__content {
//         padding-left: 0;
//         padding-right: 0;
//         padding-bottom: calc(16px + constant(safe-area-inset-bottom, 0)) !important;
//         padding-bottom: calc(16px + env(safe-area-inset-bottom, 0)) !important;
//     }
// }

.bottom-fixed {
    background-color: var(--bgc, var(--fs-white));
    position: fixed;
    // position: sticky;
    bottom: 0; /* fixed 时贴到底部 */
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
    transition: transform 0.25s ease, bottom 0.22s ease;

    &__mask {
        position: absolute;
        top: -58px;
        left: 0;
        right: 0;
        width: 100%;
        height: 58px;
        flex-shrink: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, var(--bgc, var(--fs-white)) 100%);
        pointer-events: none;
        z-index: 1;
        transition: opacity 0.3s ease;

        &.hidden {
            opacity: 0;
        }
    }

    &__content {
        width: 100%;
        min-height: 20px;
        position: relative;
        z-index: 2;
        padding: 0 16px;
    }

    /* 安全区：键盘打开时 shouldUseSafeArea = false 会移除该 class */
    &--safe-area {
        padding-bottom: env(safe-area-inset-bottom, 0);
        padding-bottom: constant(safe-area-inset-bottom, 0);

        .bottom-fixed__content {
            padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));
            padding-bottom: calc(16px + constant(safe-area-inset-bottom, 0));
        }
    }

    /* 键盘打开时：改为跟文档流（static），并使用 !important 做覆盖保险（inline style 也会同时设置） */
    &--keyboard-visible {
        position: static !important;
        box-shadow: none;
    }
}

/* 其它兼容样式保持不变 */
@-webkit-keyframes bottom-fixed-fade-in {
    from {
        opacity: 0;
        transform: translateY(100%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
@-moz-document url-prefix() {
    .bottom-fixed {
        -moz-box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
    }
}
@supports (-webkit-touch-callout: none) {
    .bottom-fixed {
        -webkit-transform: translateZ(0);
        -webkit-overflow-scrolling: touch;
        &--safe-area {
            padding-bottom: max(env(safe-area-inset-bottom), 0px);
            padding-bottom: max(constant(safe-area-inset-bottom), 0px);
        }
    }
}
@supports (-webkit-appearance: none) and (not (-webkit-touch-callout: none)) {
    .bottom-fixed {
        will-change: transform;
    }
}
</style>
